import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function PublicCatalog() {
  const { slug } = useParams();
  const [cat, setCat] = useState(null);
  const [err, setErr] = useState(false);
  const [lang, setLang] = useState("en");
  const [selected, setSelected] = useState([]);
  const [form, setForm] = useState({ name: "", contact: "", message: "" });
  const [sent, setSent] = useState(false);

  useEffect(() => {
    axios.get(`${BACKEND}/api/public/catalog/${slug}`).then(({ data }) => setCat(data)).catch(() => setErr(true));
  }, [slug]);

  const imgSrc = (it) => it.image_url ? (it.image_url.startsWith("/api") ? `${BACKEND}${it.image_url}` : it.image_url) : null;
  const toggle = (n) => setSelected((s) => s.includes(n) ? s.filter((x) => x !== n) : [...s, n]);

  const submitInquiry = async () => {
    if (!form.name || !form.contact) { toast.error("Please add your name and contact"); return; }
    try {
      await axios.post(`${BACKEND}/api/public/catalog/${slug}/inquiry`, { ...form, numbers: selected });
      setSent(true);
    } catch (e) { toast.error("Could not send, please try again"); }
  };

  if (err) return <div className="min-h-screen flex items-center justify-center text-slate-400">Catalog not found.</div>;
  if (!cat) return <div className="min-h-screen flex items-center justify-center text-slate-400">Loading…</div>;

  const desc = (it) => (lang === "es" ? it.description_es : it.description_en) || it.description_en || it.description_es || "";

  return (
    <div className="min-h-screen bg-slate-50" data-testid="public-catalog">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <div className="min-w-0">
            <div className="font-black text-xl text-slate-800 truncate">{cat.title}</div>
            {cat.description && <div className="text-sm text-slate-500 truncate">{cat.description}</div>}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button onClick={() => setLang("en")} className={`text-xs px-2 py-1 rounded ${lang === "en" ? "bg-[#2495D3] text-white" : "text-slate-500 border"}`} data-testid="pub-lang-en">EN</button>
            <button onClick={() => setLang("es")} className={`text-xs px-2 py-1 rounded ${lang === "es" ? "bg-[#2495D3] text-white" : "text-slate-500 border"}`} data-testid="pub-lang-es">ES</button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5" data-testid="pub-grid">
          {cat.items.map((it) => {
            const src = imgSrc(it);
            const inner = (
              <div className="rounded-xl border border-slate-200 bg-white overflow-hidden group h-full flex flex-col" data-testid={`pub-item-${it.number}`}>
                <div className="aspect-square bg-slate-100 relative flex items-center justify-center overflow-hidden">
                  {src ? <img src={src} alt={it.title} className="w-full h-full object-contain" loading="lazy" /> : <span className="text-slate-300">No image</span>}
                  <span className="absolute top-2 left-2 text-xs font-mono bg-black/70 text-white px-2 py-0.5 rounded-full">#{it.code || it.number}</span>
                  {cat.mode === "inquiry" && (
                    <label className="absolute top-2 right-2 bg-white/90 rounded px-1.5 py-1 flex items-center gap-1 text-[11px]" onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox" checked={selected.includes(it.number)} onChange={() => toggle(it.number)} data-testid={`pub-select-${it.number}`} /> Pick
                    </label>
                  )}
                </div>
                <div className="p-3 flex-1">
                  <div className="font-semibold text-sm text-slate-800 truncate">{it.title || `Item ${it.number}`}</div>
                  {desc(it) && <p className="text-xs text-slate-500 mt-1 line-clamp-3">{desc(it)}</p>}
                  {(cat.mode === "buy" || it.price != null) && it.price != null && <div className="text-sm font-bold text-[#2495D3] mt-1.5">${it.price}</div>}
                </div>
              </div>
            );
            if (it.link === "store" && it.product_id) {
              return <Link key={it.number} to={`/store/product/${it.product_id}`}>{inner}</Link>;
            }
            return <div key={it.number}>{inner}</div>;
          })}
        </div>

        {cat.mode === "inquiry" && (
          <div className="mt-8 rounded-xl border border-slate-200 bg-white p-5 max-w-lg" data-testid="pub-inquiry">
            {sent ? (
              <div className="text-center text-emerald-600 py-6" data-testid="pub-inquiry-sent">✓ Thank you! We received your request and will contact you soon.</div>
            ) : (
              <>
                <div className="font-semibold text-slate-800 mb-1">Request a quote</div>
                <div className="text-xs text-slate-500 mb-3">Selected items: {selected.length ? selected.map((n) => `#${n}`).join(", ") : "none yet — tap “Pick” on the items you want"}</div>
                <div className="space-y-2">
                  <input className="w-full h-10 rounded-lg border px-3 text-sm" placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="pub-name" />
                  <input className="w-full h-10 rounded-lg border px-3 text-sm" placeholder="Phone or email" value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} data-testid="pub-contact" />
                  <textarea className="w-full rounded-lg border px-3 py-2 text-sm" rows={3} placeholder="Message (optional)" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="pub-message" />
                  <button onClick={submitInquiry} className="w-full h-10 rounded-lg bg-[#2495D3] text-white font-semibold hover:bg-[#1E7AA9]" data-testid="pub-submit">Send request</button>
                </div>
              </>
            )}
          </div>
        )}
      </main>
      <footer className="text-center text-xs text-slate-400 py-6">Powered by Print and Save</footer>
    </div>
  );
}
